import {
  Component,
  type EventEmitter,
  Event,
  Host,
  Prop,
  h,
} from '@stencil/core';

@Component({
  tag: 'cms-link',
  styleUrl: 'cms-link.scss',
  shadow: true,
})
export class CmsLink {
  /**
   * set the accessible name
   */
  @Prop() accessibleName: string;

  /**
   * The type of the link. Append new types if needed.
   */
  @Prop() type: 'text' | 'standalone' | 'standalone-inverted';

  /**
   * The URL that the hyperlink points to.
   */
  @Prop() href: string;

  /**
   * The title attribute for the link, providing additional information.
   */
  @Prop() linkTitle: string;

  /**
   * Specifies where to open the linked document.
   * Default is '_self'.
   */
  @Prop() target = '_self';

  /**
   * The inner HTML content of the link.
   */
  @Prop() content: string;

  /**
   * Specifies the relationship between the current document and the linked document.
   */
  @Prop() rel = 'noopener follow';

  /**
   * The size of the link.
   * Default is 'md'.
   */
  @Prop() size: 'sm' | 'md' | 'lg' = 'md';

  /**
   * If true, prevents the text from wrapping.
   * Default is false.
   */
  @Prop() nowrap? = false;

  /**
   * Controls the spacing around the link.
   * Default is 'default'.
   */
  @Prop() spacing: 'default' | 'none' | 'top' | 'bottom' = 'default';

  /**
   * The icon to be displayed alongside the link.
   */
  @Prop() icon?: string;

  /**
   * The HTML tag to be used for the link element.
   * Default is 'a'.
   */
  @Prop() tag?: string = 'a';

  /**
   * Fired when the link is clicked.
   */
  @Event({
    eventName: 'cmsLinkClick',
    composed: true,
    bubbles: true,
  })
  cmsLinkClick: EventEmitter;

  private readonly clickHandler = () => {
    this.cmsLinkClick.emit();
  };

  render() {
    const TagType = this.tag;
    const attrs: { type?: string } = {};
    if (this.tag === 'button') {
      attrs.type = 'button';
    }
    return (
      <Host
        class={{
          [this.type]: true,
          [this.size]: true,
          [`spacing-${this.spacing}`]: true,
          nowrap: this.nowrap,
        }}
        onClick={() => {
          this.clickHandler();
        }}
      >
        <TagType
          aria-label={this.accessibleName?.trim()}
          class='link'
          href={this.href?.trim()}
          title={this.linkTitle?.trim()}
          target={this.target}
          innerHTML={this.content}
          rel={this.rel}
          {...attrs}
        />
        {this.icon ? (
          <cms-icon
            icon={this.icon}
            size={this.size === 'lg' ? 'md' : 'sm'}
            colorToken='--cms-link-text-color'
          />
        ) : null}
      </Host>
    );
  }
}
